// Adding an alert destination: pick where findings go, then configure it.
//
// Progressive on purpose. The form used to render every field at once with
// Slack preselected, so the first thing a new admin saw was a Slack webhook
// field whether or not they wanted Slack. Nothing is asked for until the
// destination is chosen, which is the same shape `IntegrationForm` uses on the
// identity surface.

import { useCallback, useMemo, useState } from "react";
import OverlayDialog from "../OverlayDialog.jsx";
import Button from "../ui/Button.jsx";
import Select from "../ui/Select.jsx";
import {
    KINDS,
    SEVERITY_RUNGS,
    kindInfo,
    specificRoutes,
    wildcardRoutes,
} from "../../lib/pushNotificationRoutes.js";
import { validateCredentialField } from "../../lib/pushNotificationUrl.js";
import {
    PUSH_VENDORS,
    vendorByKey,
    windowOptions,
} from "../../lib/pushNotificationVendors.js";
import { FormField, StatusLine, inputCls } from "./formControls.jsx";
import Checkbox from "../ui/Checkbox.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";

// Mirrors the column's VARCHAR(255) and api-server's MAX_DISPLAY_NAME, so a
// long name is stopped at the keyboard rather than by a server error.
export const MAX_DISPLAY_NAME = 255;

// Built from the kind table, so a kind added there appears without touching
// this file.
const KIND_OPTIONS = Object.entries(KINDS).map(([value, { label }]) => ({ value, label }));

// The first entry in the table. Derived, so reordering `KINDS` moves the
// picker order and the default together.
const DEFAULT_KIND = KIND_OPTIONS[0].value;
const DEFAULT_SEVERITY = kindInfo(DEFAULT_KIND)?.defaultSeverity ?? "";

/** Scope options for a kind whose sources can be named individually. */
const SCOPE_OPTIONS = [
    { value: "all", label: "All detections" },
    { value: "specific", label: "Specific detections" },
];

// Bare rung names: the "Minimum severity" label carries the "and above". No
// "everything" option -- unfiltered is not a setting anyone would leave on.
const SEVERITY_OPTIONS = SEVERITY_RUNGS.map(({ wire, label }) => ({ value: wire, label }));

/** The vendor cards shown before anything is chosen. */
function VendorPicker({ onChoose }) {
    return (
        <div className="grid gap-[10px]">
            <p className="m-0 text-[13px] leading-[1.45] text-[color:var(--muted)]">Where should Origin send findings?</p>
            <div className="grid gap-[8px] [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
                {PUSH_VENDORS.map((vendor) => (
                    <button
                        key={vendor.key}
                        type="button"
                        data-vendor={vendor.key}
                        onClick={() => onChoose(vendor.key)}
                        className="grid cursor-pointer content-start gap-[4px] rounded-[10px] border border-[color:var(--line)] bg-transparent p-[12px] text-left transition-colors hover:border-[color:var(--line-strong)] hover:bg-[color-mix(in_srgb,var(--ink)_7%,transparent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color:var(--green)]"
                    >
                        <span className="text-[13px] font-medium leading-[1.3] text-[color:var(--ink)]">
                            {vendor.label}
                        </span>
                        <span className="text-[12px] leading-[1.4] text-[color:var(--muted)]">{vendor.description}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

export default function AddDestinationDialog({ open, onClose, onSubmit, signals }) {
    const [vendorKey, setVendorKey] = useState(null);
    const [name, setName] = useState("");
    const [credentials, setCredentials] = useState({});
    // "" means "inherit the service default", which is not the same as 0.
    const [windowSeconds, setWindowSeconds] = useState("");
    const [kind, setKind] = useState(DEFAULT_KIND);
    // "all" is a wildcard route, not every box ticked: it keeps covering
    // detections shipped after this destination was created.
    const [scope, setScope] = useState("all");
    // The floor, for a kind that offers one. Seeded from the kind's own default
    // so the admin starts at a sensible noise level rather than at the top of
    // the list.
    const [severity, setSeverity] = useState(DEFAULT_SEVERITY);
    const [picked, setPicked] = useState(() => new Set());
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    // Fields the admin has left, so a problem is raised once they have had a
    // chance to fill it in. A field never visited stays quiet; its `*` says it
    // is needed.
    const [touched, setTouched] = useState(() => new Set());

    const spec = vendorKey ? vendorByKey(vendorKey) : undefined;

    const reset = useCallback(() => {
        setVendorKey(null);
        setName("");
        setCredentials({});
        setWindowSeconds("");
        setKind(DEFAULT_KIND);
        setScope("all");
        setSeverity(DEFAULT_SEVERITY);
        setPicked(new Set());
        setStatus(null);
        setError(null);
        setTouched(new Set());
    }, []);

    const close = useCallback(() => {
        reset();
        onClose();
    }, [reset, onClose]);

    // Changing the destination clears only what belongs to the old one: its
    // credential fields, because a stale value would be submitted under a key
    // the new vendor does not have, and the window, because the floor is the
    // vendor's. Name, scope and picked signals are the admin's work and survive.
    const clearVendor = useCallback((key) => {
        setVendorKey(key);
        setCredentials({});
        setWindowSeconds("");
        setError(null);
        setTouched(new Set());
    }, []);

    // The narrowings are not interchangeable: a picked-signal set carried into
    // trace review would submit routes the server refuses.
    const chooseKind = useCallback((next) => {
        setKind(next);
        setScope("all");
        setPicked(new Set());
        setSeverity(kindInfo(next)?.defaultSeverity ?? "");
    }, []);

    const togglePicked = useCallback((id) => {
        setPicked((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    // Live feedback only. api-server re-checks the same rules at the request
    // boundary, and the delivery service checks again before it dials.
    const problems = useMemo(() => {
        if (!spec) return {};
        return Object.fromEntries(
            spec.fields.map((field) => [
                field.key,
                validateCredentialField(spec.key, field, credentials[field.key] ?? ""),
            ]),
        );
    }, [spec, credentials]);

    const canSubmit =
        Boolean(spec) &&
        name.trim().length > 0 &&
        status !== "saving" &&
        // A destination subscribed to nothing would never fire. Only a kind
        // with nameable sources can be in that state.
        (!kindInfo(kind)?.hasSources || scope === "all" || picked.size > 0) &&
        spec.fields.every((field) => problems[field.key] === null);

    const submit = useCallback(async () => {
        setStatus("saving");
        setError(null);
        try {
            await onSubmit({
                display_name: name.trim(),
                vendor: spec.wire,
                // A credential DOCUMENT, keyed the way the vendor's table entry
                // says. The server re-derives the required keys from its own
                // table, so a mismatch here is refused rather than half-stored.
                credentials: Object.fromEntries(
                    spec.fields.map((field) => [field.key, (credentials[field.key] ?? "").trim()]),
                ),
                // Omitted entirely when inheriting, so the destination follows a
                // later change to the service default instead of pinning today's.
                ...(windowSeconds === "" ? {} : { window_seconds: Number(windowSeconds) }),
                routes:
                    kindInfo(kind)?.hasSources && scope === "specific"
                        ? specificRoutes([...picked], kind)
                        : wildcardRoutes(kind, severity),
            });
            close();
        } catch (err) {
            setStatus(null);
            setError(err.message || String(err));
        }
    }, [onSubmit, name, spec, credentials, windowSeconds, kind, scope, severity, picked, close]);

    return (
        <OverlayDialog
            open={open}
            onClose={close}
            analyticsId={DIALOG.NOTIFICATION_DESTINATION_NEW}
            eyebrow="Alert destinations"
            title="New destination"
            width="560px"
        >
            <div className="grid gap-[14px]">
                {!spec ? (
                    <VendorPicker onChoose={clearVendor} />
                ) : (
                    <>
                        {/* The choice stays on screen and stays changeable.
                            Collapsing it to a line keeps the picker's answer
                            visible without the cards taking the whole dialog. */}
                        <FormField as="div" label="Destination">
                            <div className="flex min-h-[32px] items-center justify-between gap-[10px] rounded-[7px] border border-[color:var(--line)] pl-[10px] pr-[3px]">
                                <span className="min-w-0 truncate text-[13px] text-[color:var(--ink)]">
                                    {spec.label}
                                </span>
                                <Button size="sm" variant="ghost" onClick={() => clearVendor(null)}>
                                    Change
                                </Button>
                            </div>
                        </FormField>

                        <FormField label="Name" required>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder="#sec-alerts"
                                className={inputCls}
                                maxLength={MAX_DISPLAY_NAME}
                            />
                        </FormField>

                        {spec.fields.map((field) => {
                            const value = credentials[field.key] ?? "";
                            const problem = problems[field.key];
                            const show =
                                (value.trim().length > 0 || touched.has(field.key)) &&
                                problem !== null;
                            return (
                                <FormField
                                    key={field.key}
                                    label={field.label}
                                    required
                                    hint={field.hint}
                                    error={show ? problem : null}
                                >
                                    <input
                                        type={field.kind === "secret" ? "password" : "text"}
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck={false}
                                        value={value}
                                        onChange={(e) =>
                                            setCredentials((prev) => ({
                                                ...prev,
                                                [field.key]: e.target.value,
                                            }))
                                        }
                                        placeholder={field.placeholder}
                                        className={inputCls}
                                        onBlur={() =>
                                            setTouched((prev) =>
                                                prev.has(field.key)
                                                    ? prev
                                                    : new Set(prev).add(field.key),
                                            )
                                        }
                                        aria-invalid={show ? "true" : undefined}
                                    />
                                </FormField>
                            );
                        })}

                        {/* A ceiling on cadence, not a schedule: nothing is
                            sent when there is nothing to send. */}
                        <FormField label="Deliver at most">
                            <Select
                                ariaLabel="Deliver at most"
                                value={windowSeconds}
                                onChange={setWindowSeconds}
                                options={windowOptions(spec.key)}
                            />
                        </FormField>

                        <FormField label="Notify about">
                            <Select
                                ariaLabel="Notify about"
                                value={kind}
                                onChange={chooseKind}
                                options={KIND_OPTIONS}
                            />
                        </FormField>

                        {/* Driven by the kind table, so a kind declaring a
                            severity inherits this control. */}
                        {kindInfo(kind)?.hasSeverity ? (
                            <FormField label="Minimum severity">
                                <Select
                                    ariaLabel="Minimum severity"
                                    value={severity}
                                    onChange={setSeverity}
                                    options={SEVERITY_OPTIONS}
                                />
                            </FormField>
                        ) : null}

                        {kindInfo(kind)?.hasSources ? (
                            <FormField label="Which ones">
                                <Select
                                    ariaLabel="Which ones"
                                    value={scope}
                                    onChange={setScope}
                                    options={SCOPE_OPTIONS}
                                />
                            </FormField>
                        ) : null}

                        {kindInfo(kind)?.hasSources && scope === "specific" ? (
                            signals.length === 0 ? (
                                <p className="m-0 text-[12.5px] leading-[1.4] text-[color:var(--muted)]">No detections to choose from yet.</p>
                            ) : (
                                <ul className="m-0 grid max-h-[180px] list-none gap-[8px] overflow-y-auto p-0">
                                    {signals.map((s) => (
                                        <li key={s.id}>
                                            <Checkbox
                                                checked={picked.has(s.id)}
                                                onChange={() => togglePicked(s.id)}
                                                label={
                                                    <span className="min-w-0 leading-[1.4]">
                                                        {s.displayName || s.name}
                                                    </span>
                                                }
                                            />
                                        </li>
                                    ))}
                                </ul>
                            )
                        ) : null}

                        <div className="flex flex-wrap items-center justify-end gap-x-[10px] gap-y-[6px] pt-[4px]">
                            <StatusLine status={status} error={error} />
                            <span className="flex items-center gap-[8px]">
                                <Button onClick={close}>Cancel</Button>
                                <Button variant="primary" onClick={submit} disabled={!canSubmit}>
                                    Add destination
                                </Button>
                            </span>
                        </div>
                    </>
                )}
            </div>
        </OverlayDialog>
    );
}
